import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OurDataService } from 'src/app/pages/secure/our-data/our-data.service';
import { PaginatedDataService } from '../../pages/secure/components/paginated-data/paginated-data.service';
import { FormCheckboxComponent } from '@agingplan/src';

/**
 * A reusable table component
 *
 * @example
 * <app-table [columns]="columns">
 *  <tr *ngFor="let row of rows">
 *   <td>{{ row.name }}</td>
 *  </tr>
 * </app-table>
 */
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent {
  @Output() checkboxChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('table') table?: ElementRef<HTMLTableElement>;
  @ViewChild('colgroup') colgroups?: ElementRef<HTMLElement>;
  @ViewChild('checkboxRef', { static: true }) checkboxRef?: ElementRef<HTMLElement>;
  @ViewChild('checkboxElement') checkboxElement?: FormCheckboxComponent;

  /**
   * The sorted columns to display in the table header
   */
  @Input() columns: TableHeaderColumn[] = [];

  /**
   * Url for sorting column endpoint (example - /api/agencybloc/policies)
   */
  @Input() search_url: string = '';

  /**
   * Sorting Order
   */
  public order_by: 'asc' | 'desc' = 'desc';

  /**
   * default sorting column
   */
  public sort_by = 'created_at';

  constructor(
    public paginatedDataService: PaginatedDataService,
    public ourDataService: OurDataService,
  ) {}

  /**
   * Sort table by column
   * @param column_name
   */
  public async shortColumnFunction(column_name = '') {
    if (column_name) {
      this.sort_by = column_name;
      this.order_by = this.order_by === 'asc' ? 'desc' : 'asc';
      this.paginatedDataService.options.sort_by = this.sort_by;
      this.paginatedDataService.options.order_by = this.order_by;
      await firstValueFrom(this.paginatedDataService.getPageAtCursor(this.search_url)).then((data) => {
        this.paginatedDataService.pagination$.next(data);
      });
    }
  }

  /**
   * Set Checkbox State
   */
  public setCheckboxState(checked: boolean): void {
    const formcheckboxEle = this.checkboxElement;
    if (formcheckboxEle) formcheckboxEle.checked = checked;
  }

  /**
   * Toggle checkbox onclick checkbox
   * @param event
   */
  public toggleAllCheckbox(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked: boolean = target.checked;
    this.checkboxChange.emit(isChecked);
  }
}

/**
 * A column in the table header
 */
export interface TableHeaderColumn {
  /**
   * A human-readable column name
   */
  label?: string;

  /**
   * The icon to display left of the column name
   */
  icon?: string;

  /**
   * The size of the column
   */
  size: 'tcol-sm' | 'tcol-lg' | 'tcol-md' | 'tcol-xl';

  /**
   * Class(es) to apply to the column
   */
  class?: string;

  /**
   * A callback function to run when the column is clicked
   */
  callback?: Function;

  /**
   * show check box if its exists
   */
  show_checkbox?: boolean;
  /**
   * Sort table by the column name. If it's in relationship then relation.column_name.
   */
  short_by?: string;

  /**
   * tooltip title
   */
  tooltip_text?: string;
}
